import dayjs from "dayjs";

export type SortDirection = "asc" | "desc";

type FieldType = "string" | "date" | "number";

interface SortConfig<T> {
  field: keyof T;
  type: FieldType;
  direction: SortDirection;
}

const getFieldValue = <T>(item: T, field: keyof T): unknown => item[field];

const compareValues = (a: unknown, b: unknown, type: FieldType): number => {
  switch (type) {
    case "string":
      return String(a || "").localeCompare(String(b || ""));
    case "date": {
      const aVal = a ? dayjs(a as string).valueOf() : 0;
      const bVal = b ? dayjs(b as string).valueOf() : 0;
      return aVal - bVal;
    }
    case "number":
      return (Number(a) || 0) - (Number(b) || 0);
    default:
      return 0;
  }
};

/**
 * Generic sort function for arrays of objects.
 *
 * @example
 * sortByField(records, { field: "employee_name", type: "string", direction: "asc" })
 * sortByField(records, { field: "date", type: "date", direction: "desc" })
 */
export const sortByField = <T>(items: T[], config: SortConfig<T>): T[] => {
  const { field, type, direction } = config;
  return [...items].sort((a, b) => {
    const aVal = getFieldValue(a, field);
    const bVal = getFieldValue(b, field);
    const result = compareValues(aVal, bVal, type);
    return direction === "asc" ? result : -result;
  });
};
